import { useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@features/auth';

import styles from './ComplianceRequiredRoute.module.css';

import Card from '@/shared/ui/Card/Card';
import Button from '@/shared/ui/Button/Button';

interface ParentalConsentRequiredRouteProps {
    children: ReactNode;
}

const ParentalConsentRequiredRoute = ({ children }: ParentalConsentRequiredRouteProps): ReactNode => {
    const { userProfile } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        const consentStatus = userProfile?.parentConsent?.status;

        // If consent status is pending, block access
        if (consentStatus === 'pending') {
            navigate('/dashboard', {
                replace: true,
                state: {
                    message: 'Parental consent is required before you can enroll in courses. Please complete the verification process.'
                }
            });
        }
    }, [userProfile?.parentConsent?.status, navigate]);

    const consentStatus = userProfile?.parentConsent?.status;

    // Block access if consent is pending
    if (consentStatus === 'pending') {
        return (
            <div className={styles.complianceContainer}>
                <Card className={styles.complianceCard}>
                    <div className={styles.content}>
                        <h1 className={styles.title}>⚠️ Parental Consent Required</h1>
                        <p className={styles.description}>
                            As a student under 18, parental consent is required before you can enroll in courses. Please complete the verification process on your dashboard.
                        </p>
                        <div className={styles.requirements}>
                            <h3>What you need to do:</h3>
                            <ul>
                                <li>Go to your dashboard</li>
                                <li>Look for the "Parental Consent Required" banner</li>
                                <li>Enter your parent's email address</li>
                                <li>Your parent will receive a verification email</li>
                                <li>Once verified, you can enroll in courses</li>
                            </ul>
                        </div>
                        <div className={styles.actions}>
                            <Button
                                onClick={() => navigate('/dashboard')}
                                variant="primary"
                            >
                                Go to Dashboard
                            </Button>
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    return children;
};

export default ParentalConsentRequiredRoute;
